import { Eye, EyeOff, Film, Snowflake, Trash2 } from 'lucide-react'
import { fmt, formatSpeed } from '../format'
import {
  clipDuration,
  MAX_FREEZE_S,
  MAX_SPEED,
  MIN_FREEZE_S,
  MIN_SPEED,
  orderedFreezes,
  SPEED_PRESETS,
  type ProjectClip,
} from '../model'
import { InspectorPanel } from './InspectorPanel'
import styles from '../styles/inspector.module.scss'

export interface ClipInspectorProps {
  clip: ProjectClip
  /** 1-based position, so the header names the same clip the list does. */
  index: number
  label: string
  onSetSpeed: (speed: number) => void
  onToggleHidden: () => void
  onFreezeDuration: (freezeId: string, duration: number) => void
  onDeleteFreeze: (freezeId: string) => void
}

/**
 * The selected clip's own properties: how fast it plays, which of its
 * frames are held, and whether it's in the cut at all.
 *
 * Speed sits on the clip rather than on a free range of the timeline
 * because a range measured in output seconds moves when you change the
 * speed that defines it. Holding a frame is listed here for the same
 * reason: a freeze belongs to the clip it lengthened, and this is where you
 * find the one you just dropped.
 */
export function ClipInspector({
  clip,
  index,
  label,
  onSetSpeed,
  onToggleHidden,
  onFreezeDuration,
  onDeleteFreeze,
}: ClipInspectorProps) {
  const freezes = orderedFreezes(clip)
  const sourceLength = Math.max(0, clip.end - clip.start)

  return (
    <InspectorPanel
      icon={<Film aria-hidden />}
      title={`${index + 1}. ${label}`}
      meta={fmt(clipDuration(clip))}
      actions={
        <button
          type="button"
          className={styles.resetBtn}
          onClick={onToggleHidden}
          aria-pressed={clip.hidden}
          aria-label={clip.hidden ? 'Incluir no corte' : 'Ocultar do corte'}
          title={
            clip.hidden
              ? 'Oculto: fora da prévia e da exportação. Clique para incluir'
              : 'Ocultar: sai do corte, continua na lista'
          }
        >
          {clip.hidden ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
        </button>
      }
    >

      <div className={styles.field}>
        <span className={styles.fieldLabel}>Velocidade</span>
        <div className={styles.chips}>
          {SPEED_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              className={styles.chip}
              aria-pressed={Math.abs(clip.speed - preset) < 0.001}
              onClick={() => {
                onSetSpeed(preset)
              }}
            >
              {formatSpeed(preset)}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.field}>
        <span className={styles.fieldLabel} />
        <div className={styles.sliderRow}>
          <input
            type="range"
            className={styles.slider}
            min={MIN_SPEED}
            max={MAX_SPEED}
            step={0.05}
            value={clip.speed}
            onChange={(e) => {
              onSetSpeed(Number(e.target.value))
            }}
            aria-label="Velocidade do clipe"
          />
          <span className={styles.sliderValue}>{formatSpeed(clip.speed)}</span>
        </div>
      </div>

      <div className={styles.fieldColumn}>
        <span className={styles.fieldLabel}>Congelados</span>
        {freezes.length === 0 ? (
          <p className={styles.empty}>
            Nenhum. Use “Congelar” para segurar o frame onde o cursor está.
          </p>
        ) : (
          freezes.map((freeze) => (
            <div key={freeze.id} className={styles.row}>
              <Snowflake aria-hidden className={styles.rowIcon} />
              {/* Where the held frame comes from, in the clip's own clock —
                  the same number the timeline hatch sits over. */}
              <span className={styles.rowLabel}>
                {fmt(Math.max(0, freeze.at - clip.start))}
              </span>
              <input
                type="range"
                className={styles.slider}
                min={MIN_FREEZE_S}
                max={MAX_FREEZE_S}
                step={0.1}
                value={freeze.duration}
                onChange={(e) => {
                  onFreezeDuration(freeze.id, Number(e.target.value))
                }}
                aria-label={`Duração do congelamento em ${fmt(freeze.at - clip.start)}`}
              />
              <span className={styles.sliderValue}>
                {freeze.duration.toFixed(1).replace('.', ',')}s
              </span>
              <button
                type="button"
                className={styles.inspectorDelete}
                onClick={() => {
                  onDeleteFreeze(freeze.id)
                }}
                aria-label="Remover congelamento"
                title="Remover congelamento"
              >
                <Trash2 aria-hidden />
              </button>
            </div>
          ))
        )}
      </div>

      <p className={styles.hintSmall}>
        {fmt(sourceLength)} de vídeo
        {clip.speed === 1 ? '' : ` a ${formatSpeed(clip.speed)}`}
        {freezes.length > 0
          ? ` + ${freezes.length === 1 ? 'um frame congelado' : `${String(freezes.length)} frames congelados`}`
          : ''}
        {clip.hidden ? ' · oculto: fora do corte e da exportação' : ''}
      </p>
    </InspectorPanel>
  )
}
